/* eslint-disable max-classes-per-file */
import React, { useEffect, useState } from 'react';
import { CatalogFileCollection, PriceData } from '../../../common/libraries/fileFormats/catalog/catalog';
import { NavigationReducer } from '../../../Redux/Reducers/NavigationReducer';
import { useAppDispatch, useAppSelector } from '../../../Redux/Store/hooks';
import { CatalogObjectDisplayObject } from '../../Data/CatalogObjectDisplayObject';
import { POSCatalogData, POSCatalogModelData } from '../../../common/libraries/modelViewer/Data/POSCatalogData';
import { CatalogObjectDisplay } from '../../Util/CatalogObjectDisplay';
import { SearchView } from '../Views/SearchView';

enum SearchResultMatchLocation {
  Name,
  ObjectInfo,
  Properties,
  Variants,
}

enum SearchResultMatchType {
  None,
  Full,
  Partial,
}

class SearchResult {
  static createResult(
    collection : CatalogFileCollection,
    catalogObject : POSCatalogModelData,
    matchLocation : SearchResultMatchLocation,
    matchType : SearchResultMatchType,
  ) : SearchResult {

    const result = new SearchResult();
    result.collection = collection;
    result.catalogObject = catalogObject;
    result.matchLocation = matchLocation;
    result.matchType = matchType;
    return result;
  }

  static getCollection(searchResult : SearchResult) {
    return searchResult.collection;
  }

  static getCatalogObject(searchResult : SearchResult) {
    return searchResult.catalogObject;
  }

  static getMatchLocation(searchResult : SearchResult) {
    return searchResult.matchLocation;
  }

  static getMatchType(searchResult : SearchResult) {
    return searchResult.matchType;
  }

  public collection : CatalogFileCollection | undefined;
  public catalogObject : POSCatalogModelData | undefined;
  public matchLocation : SearchResultMatchLocation = SearchResultMatchLocation.Name;
  public matchType : SearchResultMatchType = SearchResultMatchType.None;
}

class Search {
  static getResultMatchType = (query : string, text : string) => {
    if (!text) {
      return SearchResultMatchType.None;
    }

    const textLowerCase = text.toLowerCase();

    if (textLowerCase === query) {
      return SearchResultMatchType.Full;
    }
    if (textLowerCase.includes(query)) {
      return SearchResultMatchType.Partial;
    }

    return SearchResultMatchType.None;
  };

  static checkText(
    matchLocation : SearchResultMatchLocation,
    textToCheck : string,
    textToFind : string,
    collection : CatalogFileCollection,
    catalogObject : POSCatalogModelData,
    results : Array<SearchResult>,
  ) {

    const matchType = this.getResultMatchType(textToFind, textToCheck);
    if (matchType === SearchResultMatchType.None) {
      return false;
    }

    const newResult = SearchResult.createResult(
      collection,
      catalogObject,
      matchLocation,
      matchType,
    );
    results.push(newResult);

    return true;
  }
}

type SearchControllerParameters = {
  catalog : POSCatalogData
};

export function SearchController({ catalog } : SearchControllerParameters) {
  // From the store, get the search query
  const searchQuery = useAppSelector((state) => {
    return state.NavigationReducer.searchQuery;
  });

  // From the store, get the environment settings
  const environmentSettings = useAppSelector((state) => {
    return state.EnvironmentSettingsReducer;
  });

  // Grab the dispatch hook from Redux
  const dispatch = useAppDispatch();

  // Grab the action to change active page
  const { changeState } = NavigationReducer.actions;

  // Callback to change the active page
  const handleNavigateToObjectPage = (
    collectionName : string,
    objectName : string,
    pageType : string,
  ) => {
    dispatch(
      changeState({
        page: pageType,
        collection: collectionName,
        object: objectName,
        filter: undefined,
        searchQuery: undefined,
      }),
    );
  };

  const [results, setResults] = useState<CatalogObjectDisplayObject[]>([]);

  const performSearch = () : void => {

    if (searchQuery === undefined) {
      return;
    }

    const tempResults : SearchResult[] = [];

    const queryLowerCase = searchQuery.toLowerCase();

    const collections = catalog.getCollections();

    collections.forEach((collection) => {

      const catalogObjects = collection.objects;

      // eslint-disable-next-line no-restricted-syntax
      for (const catalogObject of catalogObjects) {

        let objectWasAMatch = false;

        const posCatalogObjectData = catalog.getModelByCollectionIDAndModelName(
          collection.id,
          catalogObject.name,
        )!;

        // Check object's name
        const catalogObjectName = catalogObject.name;

        if (
          Search.checkText(
            SearchResultMatchLocation.Name,
            catalogObjectName,
            queryLowerCase,
            collection,
            posCatalogObjectData,
            tempResults,
          )
        ) {
          continue;
        }

        // Check object info

        const catalogObjectInfos = posCatalogObjectData.getInfo();
        // eslint-disable-next-line no-restricted-syntax
        for (const catalogObjectInfo of catalogObjectInfos) {
          const catalogObjectInfoValue = catalogObjectInfo.sectionInfo;

          if (
            Search.checkText(
              SearchResultMatchLocation.ObjectInfo,
              catalogObjectInfoValue,
              queryLowerCase,
              collection,
              posCatalogObjectData,
              tempResults,
            )
          ) {
            objectWasAMatch = true;
            break;
          }
        }

        if (objectWasAMatch) {
          continue;
        }

        // Check object's properties

        const catalogObjectTags = posCatalogObjectData.getTags();
        // eslint-disable-next-line no-restricted-syntax
        for (const catalogObjectTag of catalogObjectTags) {
          const catalogObjectTagValue = catalogObjectTag.value;

          if (
            Search.checkText(
              SearchResultMatchLocation.Properties,
              catalogObjectTagValue,
              queryLowerCase,
              collection,
              posCatalogObjectData,
              tempResults,
            )
          ) {
            objectWasAMatch = true;
            break;
          }
        }

        if (objectWasAMatch) {
          continue;
        }

        // Check object's variants names

        const catalogObjectVariants = posCatalogObjectData.getVariants();
        // eslint-disable-next-line no-restricted-syntax
        for (const catalogObjectVariant of catalogObjectVariants) {
          const catalogObjectVariantName = catalogObjectVariant.name;
          if (
            Search.checkText(
              SearchResultMatchLocation.Variants,
              catalogObjectVariantName,
              queryLowerCase,
              collection,
              posCatalogObjectData,
              tempResults,
            )
          ) {
            break;
          }
        }
      }
    });

    // We need to sort the results accordingly

    tempResults.sort((resultA : SearchResult, resultB : SearchResult) => {
      // Sort by Match Location

      const matchLocationA = SearchResult.getMatchLocation(resultA);
      const matchLocationB = SearchResult.getMatchLocation(resultB);

      if (matchLocationA < matchLocationB) return -1;
      if (matchLocationA > matchLocationB) return 1;

      // Sort by Match Type

      const matchTypeA = SearchResult.getMatchType(resultA);
      const matchTypeB = SearchResult.getMatchType(resultB);

      if (matchTypeA < matchTypeB) return -1;
      if (matchTypeA > matchTypeB) return 1;

      // If all else is equal, just sort by collection name / object name

      const collectionNameA = SearchResult.getCollection(resultA)!.name;
      const collectionNameB = SearchResult.getCollection(resultB)!.name;

      const cmpCollectionName = collectionNameA.localeCompare(collectionNameB);

      if (cmpCollectionName !== 0) {
        return cmpCollectionName;
      }

      const objectNameA = SearchResult.getCatalogObject(resultA)!.getName();
      const objectNameB = SearchResult.getCatalogObject(resultB)!.getName();

      return objectNameA.localeCompare(objectNameB);
    });

    // Convert the search results to an object format the table can understand

    const resultObjects = tempResults.map((searchResult, index) => {
      const collection = SearchResult.getCollection(searchResult)!;
      const catalogObject = SearchResult.getCatalogObject(searchResult)!;

      return CatalogObjectDisplay.CreateCatalogObjectDisplayObject2(
        collection,
        catalogObject,
        index,
        environmentSettings.catalogInfoPath,
        catalog.catalogFileInfo?.priceData || new Array<PriceData>(),
      );
    });

    setResults(resultObjects);
  };

  useEffect(() => {
    performSearch();
  }, [searchQuery]);

  return (
    <SearchView
      searchQuery={searchQuery}
      results={results}
      navigateToObjectPageHandler={handleNavigateToObjectPage}
    />
  );
}
